import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress
} from '@mui/material';
import { Delete as DeleteIcon, Warning as WarningIcon } from '@mui/icons-material';
import api from '../utils/api';
import { formatTrainingTypeLabel } from './TrainingForm';

interface Training {
  id: number;
  title: string;
  description?: string;
  version?: string;
  is_current_version: boolean;
  training_type?: string;
  status?: string;
}

interface TrainingDeleteDialogProps {
  open: boolean;
  onClose: () => void;
  training: Training | null;
  onTrainingDeleted: () => void;
}

const TrainingDeleteDialog: React.FC<TrainingDeleteDialogProps> = ({
  open,
  onClose,
  training,
  onTrainingDeleted
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleDelete = async () => {
    if (!training) return;

    try {
      setLoading(true);
      setError(null);

      await api.delete(`/trainings/${training.id}/`);
      
      onTrainingDeleted();
      onClose();

    } catch (error: any) {
      console.error('Error deleting training:', error);
      if (error.response?.data) {
        const errorData = error.response.data;
        
        // Check if this is a completion prevention error
        if (errorData.detail) {
          setError(errorData.detail);
        } else if (errorData.error) {
          setError(errorData.error);
        } else if (typeof errorData === 'object') {
          // Handle other error formats
          const errorMessages = Object.entries(errorData)
            .filter(([key]) => key !== 'completed_count' && key !== 'training_id' && key !== 'training_title')
            .map(([field, messages]) => `${field}: ${Array.isArray(messages) ? messages.join(', ') : messages}`)
            .join('\n');
          setError(errorMessages || 'Delete failed. Please try again.');
        } else {
          setError(`Delete failed: ${errorData}`);
        }
      } else {
        setError('Failed to delete training. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setError(null);
      onClose();
    }
  };

  if (!training) return null;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center">
          <DeleteIcon sx={{ mr: 1, color: 'error.main' }} />
          Delete Training
        </Box>
      </DialogTitle>
      
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box sx={{ mb: 2 }}>
          <Typography variant="h6" gutterBottom>
            Are you sure you want to delete this training?
          </Typography>
          
          <Box sx={{ p: 2, bgcolor: 'error.light', borderRadius: 1, mb: 2 }}>
            <Box display="flex" alignItems="center" mb={1}>
              <WarningIcon sx={{ mr: 1, color: 'white' }} />
              <Typography variant="subtitle2" color="white">
                This action cannot be undone
              </Typography>
            </Box>
            <Typography variant="body2" color="white">
              Deleting this training will permanently remove it from the system. All associated content, sections, and quiz questions will be deleted. This action cannot be reversed.
            </Typography>
          </Box>

          <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
            <Typography variant="subtitle2" gutterBottom>
              Training Details
            </Typography>
            <Typography variant="body2" color="text.secondary">
              <strong>Title:</strong> {training.title}
            </Typography>
            {training.version && (
              <Typography variant="body2" color="text.secondary">
                <strong>Version:</strong> {training.version} {training.is_current_version && '(Current)'}
              </Typography>
            )}
            {training.training_type && (
              <Typography variant="body2" color="text.secondary">
                <strong>Type:</strong> {formatTrainingTypeLabel(training.training_type)}
              </Typography>
            )}
            {training.status && (
              <Typography variant="body2" color="text.secondary">
                <strong>Status:</strong> {training.status.toUpperCase()}
              </Typography>
            )}
            {training.description && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                <strong>Description:</strong> {training.description}
              </Typography>
            )}
          </Box>
        </Box>
      </DialogContent>
      
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button 
          onClick={handleDelete} 
          variant="contained" 
          color="error"
          disabled={loading}
          startIcon={loading ? <CircularProgress size={16} /> : <DeleteIcon />}
        >
          {loading ? 'Deleting...' : 'Delete Training'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TrainingDeleteDialog;
